/** Public page /sign/:token: the client reads the contract and signs with a WhatsApp code. */

import { ApiError, usePublicContractsCreate, usePublicContractsRetrieve } from "@agencyos/api-client";
import { formatXaf } from "@agencyos/core";
import { Backdrop, Badge, Button, GlassCard, Skeleton, SoftToggle, TextField } from "@agencyos/ui-web";
import { CircleCheck, FileText, MessageCircle } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { OfflineNotice } from "../components/AppShell";
import { LanguageSwitcher, ThemeSwitcher } from "../components/Preferences";
import { errorText } from "./contracts/shared";

export function SignPage() {
  const { t } = useTranslation();
  const { token = "" } = useParams();
  const contract = usePublicContractsRetrieve(token, { query: { enabled: Boolean(token), retry: false } });
  const requestCode = usePublicContractsCreate();
  const sign = usePublicContractsCreate();
  const [code, setCode] = useState("");
  const [agreed, setAgreed] = useState(false);
  const data = contract.data?.status === 200 ? contract.data.data : undefined;
  const codeSent = requestCode.data?.status === 200 && requestCode.data.data.status === "code_sent";
  const signed = data?.status === "signed" || (sign.data?.status === 200 && sign.data.data.status === "signed");
  const notFound = contract.error instanceof ApiError && contract.error.status === 404;

  let body;
  if (contract.isPending) {
    body = (
      <>
        <Skeleton style={{ height: 28 }} />
        <Skeleton style={{ height: 120 }} />
      </>
    );
  } else if (!data) {
    body = (
      <p role="alert" className="ko">
        {notFound ? t("sign.invalid") : t("common.error")}
      </p>
    );
  } else if (signed) {
    body = (
      <div className="stack sign-done" role="status">
        <CircleCheck size={40} className="check__icon--ok" aria-hidden="true" />
        <h1>{t("sign.doneTitle")}</h1>
        <p>{t("sign.doneText", { agency: data.agency, number: data.number })}</p>
        {data.document_url ? (
          <a className="ao-button ao-button--secondary ao-button--block" href={data.document_url} target="_blank" rel="noreferrer">
            <FileText size={16} aria-hidden="true" /> {t("sign.download")}
          </a>
        ) : null}
      </div>
    );
  } else if (data.status !== "sent") {
    body = (
      <>
        <h1>{t("sign.title")}</h1>
        <p role="note" className="ko">
          {data.status === "cancelled" ? t("sign.cancelled") : t("sign.notReady")}
        </p>
      </>
    );
  } else {
    body = (
      <>
        <h1>{t("sign.hello", { name: data.client })}</h1>
        <p className="muted">{t("sign.intro", { agency: data.agency })}</p>
        <dl className="sign-summary">
          <div>
            <dt className="muted">{t("sign.number")}</dt>
            <dd>
              <strong>{data.number}</strong>
            </dd>
          </div>
          <div>
            <dt className="muted">{t("sign.total")}</dt>
            <dd>
              <strong>{formatXaf(data.total_xaf)}</strong>
            </dd>
          </div>
        </dl>
        {data.document_url ? (
          <a className="ao-button ao-button--secondary ao-button--block" href={data.document_url} target="_blank" rel="noreferrer">
            <FileText size={16} aria-hidden="true" /> {t("sign.read")}
          </a>
        ) : (
          <p className="muted">{t("sign.pdfPending")}</p>
        )}
        <ol className="sign-steps">
          <li>{t("sign.step1")}</li>
          <li>{t("sign.step2")}</li>
          <li>{t("sign.step3")}</li>
        </ol>
        <SoftToggle label={t("sign.agree")} checked={agreed} onChange={setAgreed} />
        <Button
          variant="whatsapp"
          block
          icon={<MessageCircle size={16} aria-hidden="true" />}
          disabled={!agreed || requestCode.isPending}
          onClick={() => requestCode.mutate({ token, data: {} })}
        >
          {requestCode.isPending ? t("common.loading") : codeSent ? t("sign.resend") : t("sign.getCode")}
        </Button>
        {codeSent ? (
          <p role="status" className="ok">
            {t("sign.codeSent")}
          </p>
        ) : null}
        {requestCode.isError ? (
          <p role="alert" className="ko">
            {errorText(requestCode.error, t("common.error"))}
          </p>
        ) : null}
        {codeSent ? (
          <form
            className="stack"
            onSubmit={(e) => {
              e.preventDefault();
              if (code.length === 6) sign.mutate({ token, data: { code } }, { onSuccess: () => void contract.refetch() });
            }}
          >
            <TextField
              label={t("sign.code")}
              hint={t("sign.codeHint")}
              className="sign-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            />
            <Button type="submit" block disabled={code.length !== 6 || !agreed || sign.isPending}>
              {sign.isPending ? t("common.loading") : t("sign.sign")}
            </Button>
            {sign.isError ? (
              <p role="alert" className="ko">
                {errorText(sign.error, t("common.error"))}
              </p>
            ) : null}
          </form>
        ) : null}
        <p className="muted sign-legal">{t("sign.legal")}</p>
      </>
    );
  }

  return (
    <>
      <Backdrop intensity="vivid" />
      <main className="login sign-page">
        <div className="login-tools">
          <LanguageSwitcher />
          <ThemeSwitcher />
        </div>
        <OfflineNotice />
        <GlassCard level="thick" className="login-card stack sign-card">
          <div className="row between">
            <p className="login-brand">{data?.agency ?? t("app.name")}</p>
            {data && !signed ? <Badge tone="offer">{t("sign.badge")}</Badge> : null}
          </div>
          {body}
        </GlassCard>
      </main>
    </>
  );
}
